import { initializeApp } from 'firebase/app';
import { getFirestore } from '@firebase/firestore';

// Firebase web config — sourced exclusively from the Vite env at build time.
// In local development that's `.env.local` (gitignored); in production it's
// Netlify's environment-variable panel.
//
// NOTE: the Firebase web config is *not* a secret — it's a public identifier
// the SDK uses to find your project. The real protection layer is the
// Firestore security rules in /firestore.rules. We pull the config from
// env vars for hygiene (easy project swaps, no diff churn on rotation),
// not as a confidentiality measure.
//
// If you see "Missing VITE_FIREBASE_*" thrown on boot: copy `.env.example`
// to `.env.local`, fill in the values from your Firebase console, and
// restart the dev server. On Netlify, set the same keys in
// Site settings → Environment variables.

// Required env keys, validated lazily inside getDb() (never at module load).
const required = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
];

// Memoized lazy Firestore handle. The S2 firebase provider modules call this
// at CALL TIME (never at module top level) so that importing the provider has
// no side effects — a Supabase-only build can statically import the firebase
// provider without constructing a Firestore app or throwing on missing env.
//
// Validates the required VITE_FIREBASE_* vars only WHEN CALLED, and
// initializes initializeApp + getFirestore ONCE. (S3 removed the old eager
// top-level `db` export so importing this module constructs nothing and never
// throws at load — see docs/features/supabase/.)
let _db = null;
export function getDb() {
  if (_db) return _db;
  const e = import.meta.env || {};
  const lazyMissing = required.filter((k) => !e[k]);
  if (lazyMissing.length > 0) {
    throw new Error(
      `Missing Firebase env vars: ${lazyMissing.join(', ')}. ` +
        'Copy .env.example to .env.local and fill in values, or set them in your host env panel.',
    );
  }
  const lazyApp = initializeApp({
    apiKey: e.VITE_FIREBASE_API_KEY,
    authDomain: e.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: e.VITE_FIREBASE_PROJECT_ID,
    storageBucket: e.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: e.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: e.VITE_FIREBASE_APP_ID,
  });
  _db = getFirestore(lazyApp);
  return _db;
}
